import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface Profile {
  id: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

const profileKeys = {
  all: ["profile"] as const,
  byUser: (userId: string) => ["profile", userId] as const,
};

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching profile:", error);
    return null;
  }
  return data;
}

export const useProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile = null, isLoading } = useQuery({
    queryKey: profileKeys.byUser(user?.id ?? ""),
    queryFn: () => fetchProfile(user!.id),
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const invalidate = useCallback(() => {
    if (user) queryClient.invalidateQueries({ queryKey: profileKeys.byUser(user.id) });
  }, [user, queryClient]);

  const createProfile = useCallback(async (username: string): Promise<boolean> => {
    if (!user) return false;
    try {
      const { error } = await supabase
        .from("profiles")
        .insert({ user_id: user.id, username: username.trim() });
      if (error) { console.error("Error creating profile:", error); return false; }
      invalidate();
      return true;
    } catch (err) { console.error("Error:", err); return false; }
  }, [user, invalidate]);

  const updateUsername = useCallback(async (username: string): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: "Nie jesteś zalogowany" };

    const trimmedUsername = username.trim();
    if (trimmedUsername.length < 3) return { success: false, error: "Nazwa musi mieć co najmniej 3 znaki" };
    if (trimmedUsername.length > 30) return { success: false, error: "Nazwa może mieć maksymalnie 30 znaków" };
    if (!/^[a-zA-Z0-9ąćęłńóśźżĄĆĘŁŃÓŚŹŻ _\-.*~@#&!?+]+$/.test(trimmedUsername)) {
      return { success: false, error: "Niedozwolone znaki (bez emoji i symboli specjalnych)" };
    }

    try {
      const { data: isAvailable, error: checkError } = await supabase
        .rpc("check_username_available", { p_username: trimmedUsername, p_exclude_user_id: user.id });
      if (checkError) return { success: false, error: "Błąd sprawdzania dostępności nazwy" };
      if (!isAvailable) return { success: false, error: "Ta nazwa jest już zajęta" };

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ username: trimmedUsername })
        .eq("user_id", user.id);
      if (updateError) {
        if (updateError.code === "23505") return { success: false, error: "Ta nazwa jest już zajęta" };
        return { success: false, error: "Błąd aktualizacji profilu" };
      }

      invalidate();
      return { success: true };
    } catch (err) { console.error("Error:", err); return { success: false, error: "Wystąpił nieoczekiwany błąd" }; }
  }, [user, invalidate]);

  const checkUsernameAvailable = useCallback(async (username: string): Promise<boolean> => {
    if (!user) return false;
    const trimmed = username.trim();
    if (trimmed.length < 3) return false;
    try {
      const { data, error } = await supabase
        .rpc("check_username_available", { p_username: trimmed, p_exclude_user_id: user.id });
      if (error) return false;
      return data ?? false;
    } catch { return false; }
  }, [user]);

  const updateAvatarUrl = useCallback(async (avatarUrl: string | null): Promise<boolean> => {
    if (!user) return false;
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: avatarUrl })
        .eq("user_id", user.id);
      if (error) { console.error("Error updating avatar:", error); return false; }
      invalidate();
      return true;
    } catch (err) { console.error("Error:", err); return false; }
  }, [user, invalidate]);

  return {
    profile,
    username: profile?.username || null,
    avatarUrl: profile?.avatar_url || null,
    isLoading,
    createProfile,
    updateUsername,
    updateAvatarUrl,
    checkUsernameAvailable,
    refetch: invalidate,
  };
};

// Hook for admin to get all profiles
export const useAdminProfiles = () => {
  const { isAdmin } = useAuth();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("username", { ascending: true });
      if (error) { console.error("Error fetching profiles:", error); return []; }
      return data || [];
    },
    enabled: isAdmin,
    staleTime: 60_000,
  });

  const profiles = data || [];
  const profilesMap = useMemo(() => {
    const map: Record<string, Profile> = {};
    profiles.forEach(p => { map[p.user_id] = p; });
    return map;
  }, [profiles]);

  const getUsername = useCallback((userId: string): string => {
    return profilesMap[userId]?.username || userId.slice(0, 8) + "...";
  }, [profilesMap]);

  return { profiles, profilesMap, isLoading, getUsername, refetch };
};
